import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Los filtros del cierre detallado.
 *
 * 🔴 Los nombres coinciden LETRA POR LETRA con los query params del servidor
 * (`modules/integracion-pos/http/rutas.js`, `GET /admin/caja/cierre-detallado`),
 * igual que en `solicitudes.service.ts`: así la URL de la pantalla se pega tal cual
 * en un `curl` para reproducir lo que alguien está viendo.
 *
 * ⚠️ `caja` y `tienda` se llaman así en la consulta, pero el servidor los responde
 * dentro de `filtros` como `cash_register` y `shop`. No se renombran aquí: el que
 * compara la pantalla contra la respuesta tiene que ver los mismos nombres que
 * mandó.
 */
export interface FiltrosCierre {
  /** YYYY-MM-DD. Manda sobre `desde`/`hasta`. Sin fecha, el servidor usa hoy en Venezuela. */
  fecha?: string;
  cedula?: string;
  caja?: string;
  tienda?: string;
  /**
   * 🔴 EL COMERCIO, EXPLÍCITO — agregado el 2026-09-21, cuando el cierre pasó a
   * ser una pestaña de la ficha del comercio (orden de Juan: *«ese cierre de
   * caja no debería estar»* suelto en el menú).
   *
   * Hasta entonces el comercio lo ponía SÓLO el interceptor, desde el selector
   * de la cabecera — y por eso la pantalla quedaba inerte con «Todos los
   * comercios» puesto. Dentro de la ficha el comercio ya está elegido: es el
   * `:code` de la dirección, y mandarlo acá le gana al selector porque el
   * servidor lee `req.query.merchant_code || req.query.merchant`.
   */
  merchant_code?: string;
}

/**
 * EL CIERRE DE CAJA DETALLADO — crédito por crédito.
 *
 * *«Los administradores también pueden bajarse un cierre de caja donde le traiga
 * todos los créditos, cuándo fue la inicial y cuánto fue, y cuánto se financió»*
 * — Juan, 2026-09-20.
 *
 * 🔴 ES DE SÓLO LECTURA, y eso no es un detalle: son dos GET. No abre ni cierra un
 * turno y no recalcula ningún cuadre congelado (premisa de Juan de la misma fecha:
 * *«todo lo que ajustes no puede tocar la lógica que actualmente ya está»*).
 *
 * 🔴 VA POR `/admin/`, NO POR `/comercio/`, y la diferencia no es cosmética.
 *
 * La primera versión de este servicio apuntaba a `/comercio/caja/cierre-detallado`,
 * que resuelve el comercio desde `usuario.merchant`. **Un usuario de esta consola
 * nunca tiene `merchant`**: es justamente así como el núcleo reconoce a la gente de
 * CrediTotal (`casos/listado-solicitudes.js:esDeCrediTotal`). La pantalla habría
 * salido SIEMPRE VACÍA, sin un error que lo explicara — el guard de la consola la
 * dejaba entrar y el endpoint la rechazaba por no tener comercio. Lo encontró la
 * certificación, no el compilador.
 *
 * El gemelo `/admin/` toma el comercio de la consulta, y sólo se lo acepta a quien
 * `esDeCrediTotal` reconoce. El código va en `merchant_code` — y lo pone el
 * interceptor de comercio activo, que ya le pega `?merchant=` a toda llamada a
 * `BASE_LA` (`interceptor/comercio-interceptor.service.ts`): el caso lee los dos
 * nombres, así que no hay que mandarlo a mano.
 */
@Injectable({ providedIn: 'root' })
export class CierreCajaService {

  private static readonly RUTA = '/api/integracion-pos/admin/caja/cierre-detallado';

  private readonly api = `${environment.BASE_LA}${CierreCajaService.RUTA}`;

  constructor(private httpClient: HttpClient) { }

  /** Los valores vacíos no viajan: un `cedula=` vacío es un filtro puesto para el servidor. */
  private parametros = (f: FiltrosCierre = {}, extra: Record<string, string> = {}): HttpParams => {
    let p = new HttpParams();
    const agregar = (clave: string, valor: any): void => {
      const v = valor === null || valor === undefined ? '' : String(valor).trim();
      if (v) p = p.set(clave, v);
    };
    agregar('fecha', f.fecha);
    agregar('cedula', f.cedula);
    agregar('caja', f.caja);
    agregar('tienda', f.tienda);
    agregar('merchant_code', f.merchant_code);
    Object.keys(extra).forEach((k) => agregar(k, extra[k]));
    return p;
  };

  /**
   * El cierre como JSON: `{ dia, desde, hasta, filtros, creditos[], total, alcance }`.
   *
   * 📍 No pagina. El día de un comercio son decenas de créditos, no miles — y un
   * cierre partido en páginas deja de ser un cierre: el total al pie tiene que
   * cuadrar contra las filas que se están viendo.
   */
  getCierreDetallado = async (filtros: FiltrosCierre = {}): Promise<any> =>
    lastValueFrom(this.httpClient.get<any>(this.api, { params: this.parametros(filtros) }));

  /**
   * El mismo cierre, como archivo, y lo guarda el navegador.
   *
   * 🔴 VA POR `HttpClient` Y NO POR UN `<a href>` PELADO: el token viaja en el
   * encabezado `authorization` (lo pone `AuthInterceptorService`), y una descarga
   * del navegador no manda encabezados — el enlace directo devolvería 401. Por eso
   * se trae el archivo con la sesión puesta y recién ahí se le da a guardar.
   *
   * ⚠️ MEDIDO EL 2026-09-20: el servidor SÍ manda `Content-Disposition` con el
   * nombre bueno (lleva el comercio y el día), pero la API responde con
   * `cors({ origin: '*' })` y SIN `exposedHeaders` (`models/server.js:302`), así que
   * el navegador esconde ese encabezado de JavaScript en una petición de otro
   * origen. Se intenta leerlo igual —por si un día se expone— y si no viene, el
   * nombre se arma aquí: quien baja cinco cierres en una semana tiene que poder
   * distinguirlos en su carpeta sin abrirlos.
   */
  bajarCsv = async (filtros: FiltrosCierre = {}): Promise<void> => {
    const respuesta = await lastValueFrom(this.httpClient.get(this.api, {
      params: this.parametros(filtros, { formato: 'csv' }),
      responseType: 'blob',
      observe: 'response',
    }));

    const nombre = this.nombreDeArchivo(respuesta.headers.get('content-disposition'))
      || `cierre-${filtros.fecha || 'del-dia'}.csv`;

    const url = URL.createObjectURL(respuesta.body as Blob);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = nombre;
    document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
    // Se suelta en el siguiente turno del navegador, no en la misma línea:
    // revocarlo enseguida aborta la descarga en Firefox.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  /** `attachment; filename="cierre-MT-2026-09-20.csv"` → `cierre-MT-2026-09-20.csv`. */
  private nombreDeArchivo = (cabecera: string | null): string | null => {
    if (!cabecera) return null;
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cabecera);
    return m ? decodeURIComponent(m[1].trim()) : null;
  };

  /**
   * El mensaje de un fallo de la descarga.
   *
   * 🔴 Con `responseType: 'blob'` el CUERPO DEL ERROR TAMBIÉN ES UN BLOB: leer
   * `e.error.error.message` como en el resto de la consola devuelve `undefined`
   * siempre, y el operador se queda con un «no se pudo» sin causa. Acá se lee el
   * blob como texto y recién ahí se busca el mensaje del módulo
   * (`dominio/codigos.js`) o el `{msg}` del middleware de JWT.
   */
  mensajeDeFalloAlBajar = async (e: any): Promise<string> => {
    const cuerpo = e?.error;
    if (!(cuerpo instanceof Blob)) {
      return cuerpo?.error?.message || cuerpo?.msg || '';
    }
    try {
      const json = JSON.parse(await cuerpo.text());
      return json?.error?.message || json?.msg || '';
    } catch {
      return '';
    }
  };
}
